/** Progressive enhancement: the unenhanced anchors expose every information section. */
class AvlProductInformation extends HTMLElement {
  #abort;
  #tabs = [];
  #panels = [];

  connectedCallback() {
    this.#abort?.abort();
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    this.#tabs = [...this.querySelectorAll('[data-info-tab]')];
    this.#panels = this.#tabs.map((tab) => this.querySelector(tab.getAttribute('href')));
    if (!this.#tabs.length || this.#panels.some((panel) => !panel)) return;
    this.querySelector('[data-info-tabs]')?.setAttribute('role', 'tablist');
    this.#tabs.forEach((tab, index) => {
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-controls', this.#panels[index].id);
      this.#panels[index].setAttribute('role', 'tabpanel');
      this.#panels[index].setAttribute('tabindex', '0');
    });
    this.addEventListener('click', this.#onTabClick, { signal });
    this.addEventListener('keydown', this.#onKeyDown, { signal });
    document.addEventListener('click', this.#onAnchorClick, { signal });
    window.addEventListener('hashchange', this.#onHashChange, { signal });
    this.select(0);
    this.#revealHash(location.hash, true);
  }

  disconnectedCallback() { this.#abort?.abort(); }

  select(index, focus = false) {
    if (!this.#tabs[index]) return;
    this.#tabs.forEach((tab, tabIndex) => {
      const selected = tabIndex === index;
      tab.setAttribute('aria-selected', String(selected));
      tab.setAttribute('tabindex', selected ? '0' : '-1');
      this.#panels[tabIndex].hidden = !selected;
    });
    if (focus) this.#tabs[index].focus();
  }

  #onTabClick = (event) => {
    const tab = event.target.closest('[data-info-tab]');
    const index = this.#tabs.indexOf(tab);
    if (index < 0) return;
    event.preventDefault();
    this.select(index);
  };

  #onKeyDown = (event) => {
    const index = this.#tabs.indexOf(event.target.closest('[data-info-tab]'));
    if (index < 0) return;
    let next;
    if (event.key === 'ArrowRight') next = (index + 1) % this.#tabs.length;
    if (event.key === 'ArrowLeft') next = (index - 1 + this.#tabs.length) % this.#tabs.length;
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = this.#tabs.length - 1;
    if (next === undefined) return;
    event.preventDefault();
    this.select(next, true);
  };

  #onAnchorClick = (event) => {
    if (event.defaultPrevented) return;
    const anchor = event.target.closest('a[href]');
    if (!anchor || anchor.hasAttribute('data-info-tab')) return;
    let url;
    try { url = new URL(anchor.href, location.href); } catch { return; }
    if (url.origin !== location.origin || url.pathname !== location.pathname || url.search !== location.search) return;
    this.#revealHash(url.hash, false);
  };
  #onHashChange = () => this.#revealHash(location.hash, true);
  #revealHash(hash, scroll) {
    if (!hash || hash === '#') return;
    let target;
    try { target = document.getElementById(decodeURIComponent(hash.slice(1))); } catch { return; }
    if (!target) return;
    const index = this.#panels.findIndex((panel) => panel === target || panel.contains(target));
    if (index < 0) return;
    this.select(index);
    if (scroll) requestAnimationFrame(() => {
      if (this.isConnected) target.scrollIntoView({ block: 'start', behavior: 'auto' });
    });
  }
}
if (!customElements.get('avl-product-information')) customElements.define('avl-product-information', AvlProductInformation);
